import React, {useEffect, useState} from 'react';
import {TextField, Button, Box, Typography, Grid, CircularProgress, Breadcrumbs, Checkbox, FormControlLabel} from '@mui/material';
import {Link, useNavigate, useParams} from 'react-router-dom';
import api from '../../api';
import Loading from "../../components/Loading";
import {NumericFormat} from "react-number-format";
import DidNotLoad from "../../components/DidNotLoad";
import PrinterCatalogSelect from '../../components/printers/PrinterCatalogSelect';

function EditPrinterPage() {
    const [printer, setPrinter] = useState({
        name: '',
        brand: '',
        model: '',
        printer_model_uuid: '',
        avgConsumption: '',
        consumption: '',
        price: '',
        active: true,
    });
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [resultErrors, setResultErrors] = useState({
        name: null,
        brand: null,
        model: null,
        printer_model_uuid: null,
        avg_consumption_watts: null,
        price: null,
        consumption: null,
        has_errors: false
    });
    const [hasResponse, setHasResponse] = useState(false);

    useEffect(() => {
        api.get(`printers/${id}`)
            .then(response => {
                const data = response.data.data || {};
                setPrinter({
                    name: data.name || '',
                    brand: data.brand || '',
                    model: data.model || '',
                    printer_model_uuid: data.printer_model_uuid || '',
                    avgConsumption: data.avg_consumption_watts ?? '',
                    consumption: data.power_consumption_watts ?? data.consumption ?? '',
                    price: data.acquisition_value ?? data.price ?? 0,
                    active: data.active !== false,
                });
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [id]);

    const handleSubmit = (e) => {
        e.preventDefault();

        let hasError = false
        if (printer.name === '') {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors,
                name: 'O nome da impressora é obrigatório para distinguir uma da outra.',
                has_errors: true,
            }));
        }

        if (!printer.printer_model_uuid) {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors,
                printer_model_uuid: 'Selecione a marca e o modelo da impressora.',
                has_errors: true,
            }));
        }

        if (hasError) {
            return;
        }

        if (isSubmitting) return;
        setIsSubmitting(true);

        const today = new Date().toISOString().slice(0, 10);

        const updatedPrinter = {
            name: printer.name,
            printer_model_uuid: printer.printer_model_uuid,
            avg_consumption_watts: parseInt(printer.avgConsumption, 10),
            power_consumption_watts: parseInt(printer.consumption, 10),
            acquisition_value: parseInt(printer.price, 10),
            acquisition_date: today,
            hourly_printing_cost: 0,
            active: printer.active,
        };

        api.put(`printers/${id}`, updatedPrinter)
            .then(response => {
                setHasResponse(true);
                console.log('Impressora atualizada com sucesso!', response.data);
                navigate('/admin/printers');
            })
            .catch(error => {
                try {
                    const data = error.response.data;
                    setResultErrors({
                        ...data.errors,
                        has_errors: true,
                    });
                    setHasResponse(true);
                } catch (error) {
                    console.log(error);
                }
            })
            .finally(() => {
                setIsSubmitting(false);
                if (!hasResponse) {
                    setDidNotLoad(true);
                }
            });
    };

    if (isLoading) {
        return <Loading/>;
    }

    if (didNotLoad) {
        return <DidNotLoad/>
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/printers')} to={'/admin/printers'}>
                    Impressoras
                </Link>
                <Typography color="text.primary">Editando impressora</Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom>Editar Impressora</Typography>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            label="Nome"
                            fullWidth
                            value={printer.name}
                            onChange={(e) => setPrinter({ ...printer, name: e.target.value })}
                        />
                        {resultErrors.name && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.name}
                            </div>
                        )}
                    </Grid>
                    <PrinterCatalogSelect
                        brand={printer.brand}
                        model={printer.model}
                        printerModelUuid={printer.printer_model_uuid}
                        errors={resultErrors}
                        onChange={(next) => {
                            setPrinter((prev) => ({
                                ...prev,
                                brand: next.brand || '',
                                model: next.model || '',
                                printer_model_uuid: next.printer_model_uuid || '',
                                avgConsumption: next.avg_consumption_watts
                                    ? String(next.avg_consumption_watts)
                                    : prev.avgConsumption,
                                consumption: next.max_consumption_watts
                                    ? String(next.max_consumption_watts)
                                    : prev.consumption,
                            }));
                        }}
                    />
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Consumo médio (W)"
                            type="number"
                            fullWidth
                            value={printer.avgConsumption}
                            onChange={(e) => setPrinter({ ...printer, avgConsumption: e.target.value })}
                            inputProps={{ min: 1, step: 1 }}
                            inputMode="numeric"
                        />
                        {resultErrors.avg_consumption_watts && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.avg_consumption_watts}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Consumo máximo (W)"
                            type="number"
                            fullWidth
                            value={printer.consumption}
                            onChange={(e) => setPrinter({ ...printer, consumption: e.target.value })}
                            inputProps={{ min: 1, step: 1 }}
                            inputMode="numeric"
                        />
                        {resultErrors.consumption || resultErrors.power_consumption_watts ? (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.consumption || resultErrors.power_consumption_watts}
                            </div>
                        ) : null}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <NumericFormat
                            label="Valor pago"
                            fullWidth
                            customInput={TextField}
                            value={(printer.price || 0) / 100}
                            onValueChange={(values) => {
                                const priceInCents = Math.round((values.floatValue || 0) * 100);
                                setPrinter((prev) => ({ ...prev, price: priceInCents }));
                            }}
                            thousandSeparator="."
                            decimalSeparator=","
                            prefix="R$ "
                            decimalScale={2}
                            fixedDecimalScale
                        />
                        {resultErrors.price && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.price}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={printer.active}
                                    onChange={(e) => setPrinter({ ...printer, active: e.target.checked })}
                                    color="primary"
                                />
                            }
                            label="Impressora ativa (inativas vão para Arquivadas e não entram na produção)"
                        />
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                   <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                        startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                        fullWidth
                    >
                        {isSubmitting ? 'Atualizando...' : 'Atualizar Impressora'}
                    </Button>
                </Box>
            </form>
        </div>
    );
}

export default EditPrinterPage;
